import type { DepositRow } from '@/lib/services/deposits/deposits.types'
import { InfoNotice } from '@/components/patterns/info-notice'
import { getMovementName, isBankWithdrawal, isManualDeposit } from './-presentation'

const BANK_ACCOUNT_FAILURE_CODES = new Set<DepositRow['failure_code']>([
  'account_closed',
  'account_frozen',
  'bank_account_restricted',
  'bank_account_unusable',
  'bank_ownership_changed',
  'debit_not_authorized',
  'incorrect_account_holder_address',
  'incorrect_account_holder_name',
  'incorrect_account_holder_tax_id',
  'incorrect_account_type',
  'invalid_account_number',
  'invalid_account_number_length',
  'invalid_currency',
  'no_account',
  'unsupported_card',
])

export function DepositNotices({
  deposit,
}: {
  deposit: Pick<
    DepositRow,
    | 'status'
    | 'reconciliation_difference_minor'
    | 'movement_kind'
    | 'automatic'
    | 'method'
    | 'entries_sync_status'
    | 'failure_code'
  >
}) {
  const hasDifference = Number(deposit.reconciliation_difference_minor) !== 0
  const isWithdrawal = isBankWithdrawal(deposit)
  const movementName = getMovementName(deposit)
  const movementTitle = movementName.replace(/^./, (letter) => letter.toUpperCase())

  if (deposit.status === 'failed') {
    return (
      <InfoNotice compact tone="danger" title={`${movementTitle} failed`}>
        {isWithdrawal
          ? getWithdrawalFailureMessage(deposit.failure_code)
          : getDepositFailureMessage(deposit.failure_code)}
      </InfoNotice>
    )
  }

  if (deposit.status === 'canceled') {
    return (
      <InfoNotice compact tone="danger" title={`${movementTitle} canceled`}>
        {isWithdrawal
          ? 'This bank withdrawal was canceled before the amount could be collected.'
          : 'This deposit was canceled before it reached your bank. The amount was returned to your balance.'}
      </InfoNotice>
    )
  }

  if (deposit.status !== 'paid') return null

  // Manual and Instant Payouts explain themselves in their transactions card.
  if (isManualDeposit(deposit) && deposit.entries_sync_status === 'unavailable') return null

  if (hasDifference) {
    return (
      <InfoNotice compact tone="warning" title={`${movementTitle} total needs review`}>
        The amounts shown below do not add up to the {movementName} total.
      </InfoNotice>
    )
  }

  if (deposit.entries_sync_status !== 'complete') {
    return (
      <InfoNotice compact tone="warning" title={`${movementTitle} breakdown unavailable`}>
        We do not have enough information to show what makes up this {movementName}. Contact us if
        you need help identifying what it includes.
      </InfoNotice>
    )
  }

  return null
}

function getDepositFailureMessage(failureCode: DepositRow['failure_code']): string {
  if (failureCode === 'insufficient_funds') {
    return "At the time this deposit was sent, there wasn't enough money in your available balance to complete it. The amount was returned to your balance. Contact us if you need help confirming when it can be sent again."
  }

  if (failureCode === 'declined') {
    return 'Your bank declined this deposit. The amount was returned to your balance. Contact your bank before another deposit is sent, or contact us for help.'
  }

  if (BANK_ACCOUNT_FAILURE_CODES.has(failureCode)) {
    return 'The bank account on file could not receive this deposit. The amount was returned to your balance. Review your bank account settings or contact us before expecting another deposit.'
  }

  return 'This deposit did not reach your bank. The amount was returned to your balance. Contact us if you need help resolving the issue.'
}

function getWithdrawalFailureMessage(failureCode: DepositRow['failure_code']): string {
  if (failureCode === 'insufficient_funds') {
    return 'We could not collect this amount because the bank account did not have enough funds at the time. It remains due and may reduce a future deposit or be collected in another withdrawal.'
  }

  if (failureCode === 'declined') {
    return 'The bank declined this withdrawal. The amount remains due and may reduce a future deposit or be collected in another withdrawal. Contact your bank or contact us for help.'
  }

  if (BANK_ACCOUNT_FAILURE_CODES.has(failureCode)) {
    return 'We could not collect this amount because the bank account on file could not be used. The amount remains due and may reduce a future deposit. Review your bank account settings or contact us for help.'
  }

  return 'We could not collect this amount from your bank. It remains due and may reduce a future deposit or be collected in another withdrawal. Contact us if you need help.'
}
